/**
 * Utility functions for date handling
 */

/**
 * Formats a date as a string with specified format
 * @param {Date|string|number} date - The date to format
 * @param {string} format - The format string (default: 'YYYY-MM-DD')
 * @returns {string} - The formatted date string
 */
export const formatDate = (date, format = "YYYY-MM-DD") => {
  if (!date) return "";

  const d = date instanceof Date ? date : new Date(date);

  if (isNaN(d.getTime())) return "";

  const year = d.getFullYear();
  const month = d.getMonth() + 1; // getMonth() returns 0-11
  const day = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  // Pad single digits with leading zero
  const pad = (num) => num.toString().padStart(2, "0");

  return format
    .replace("YYYY", year)
    .replace("MM", pad(month))
    .replace("DD", pad(day))
    .replace("HH", pad(hours))
    .replace("mm", pad(minutes))
    .replace("ss", pad(seconds));
};

/**
 * Returns a relative time string (e.g., "3 hours ago", "in 5 minutes")
 * @param {Date|string|number} date - The date to calculate relative time from
 * @param {Date} [baseDate=new Date()] - The base date to compare against
 * @returns {string} - The relative time string
 */
export const getRelativeTime = (date, baseDate = new Date()) => {
  if (!date) return "";

  const d = date instanceof Date ? date : new Date(date);

  if (isNaN(d.getTime())) return "";

  const base = baseDate instanceof Date ? baseDate : new Date(baseDate);
  const difference = Math.floor((d.getTime() - base.getTime()) / 1000);

  // Absolute difference in seconds
  const absDiff = Math.abs(difference);

  // Time ago/from now
  const isPast = difference < 0;
  const prefix = isPast ? "" : "in ";
  const suffix = isPast ? " ago" : "";

  // Calculate time units
  if (absDiff < 60) {
    return difference === 0
      ? "just now"
      : `${prefix}${absDiff} seconds${suffix}`;
  } else if (absDiff < 3600) {
    const minutes = Math.floor(absDiff / 60);
    return `${prefix}${minutes} minute${minutes !== 1 ? "s" : ""}${suffix}`;
  } else if (absDiff < 86400) {
    const hours = Math.floor(absDiff / 3600);
    return `${prefix}${hours} hour${hours !== 1 ? "s" : ""}${suffix}`;
  } else if (absDiff < 2592000) {
    const days = Math.floor(absDiff / 86400);
    return `${prefix}${days} day${days !== 1 ? "s" : ""}${suffix}`;
  } else if (absDiff < 31536000) {
    const months = Math.floor(absDiff / 2592000);
    return `${prefix}${months} month${months !== 1 ? "s" : ""}${suffix}`;
  } else {
    const years = Math.floor(absDiff / 31536000);
    return `${prefix}${years} year${years !== 1 ? "s" : ""}${suffix}`;
  }
};

/**
 * Compares two dates and returns true if they are the same day
 * @param {Date|string|number} date1 - The first date
 * @param {Date|string|number} date2 - The second date
 * @returns {boolean} - True if the dates are the same day
 */
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;

  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
};

/**
 * Adds time to a date
 * @param {Date|string|number} date - The date to add time to
 * @param {number} amount - The amount to add
 * @param {string} unit - The unit (day, month, year, hour, minute, second)
 * @returns {Date} - The new date
 */
export const addTime = (date, amount, unit) => {
  if (!date) return new Date();

  const d = date instanceof Date ? new Date(date) : new Date(date);

  if (isNaN(d.getTime())) return new Date();

  switch (unit.toLowerCase()) {
    case "year":
    case "years":
      d.setFullYear(d.getFullYear() + amount);
      break;
    case "month":
    case "months":
      d.setMonth(d.getMonth() + amount);
      break;
    case "day":
    case "days":
      d.setDate(d.getDate() + amount);
      break;
    case "hour":
    case "hours":
      d.setHours(d.getHours() + amount);
      break;
    case "minute":
    case "minutes":
      d.setMinutes(d.getMinutes() + amount);
      break;
    case "second":
    case "seconds":
      d.setSeconds(d.getSeconds() + amount);
      break;
    default:
    // No change if unknown unit
  }

  return d;
};

/**
 * Extracts just the date part from a timestamp or date object
 * @param {string|Date} timestamp - The timestamp or date to process
 * @returns {string} - The date part in YYYY-MM-DD format
 */
export const getDatePart = (timestamp) => {
  if (!timestamp) return "";

  // If it's a string, extract the date part
  if (typeof timestamp === "string") {
    // Handle ISO format or various datetime formats
    const parts = timestamp.split(/[ T]/); // Split by space or T
    if (parts.length > 0) {
      return parts[0]; // Return the date part
    }
    return "";
  }

  // If it's a Date object
  if (timestamp instanceof Date) {
    return formatDate(timestamp, "YYYY-MM-DD");
  }

  // Try to convert to Date and format
  try {
    const date = new Date(timestamp);
    return formatDate(date, "YYYY-MM-DD");
  } catch (e) {
    return "";
  }
};

/**
 * Sorts an array of dates or timestamps
 * @param {Array<string|Date>} dates - Array of dates to sort
 * @param {boolean} ascending - Sort in ascending order if true
 * @returns {Array<string|Date>} - Sorted array
 */
export const sortDates = (dates, ascending = true) => {
  if (!Array.isArray(dates) || dates.length === 0) return [];

  return [...dates].sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);

    if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
      return 0; // Can't compare invalid dates
    }

    return ascending ? dateA - dateB : dateB - dateA;
  });
};

/**
 * Groups data points by day for time series
 * @param {Array<Object>} data - Array of data points with date/timestamp properties
 * @param {string} dateField - Field name containing the date/timestamp
 * @param {string} valueField - Field name containing the value
 * @param {Function} [aggregator] - Function to aggregate multiple values for same day (defaults to last value)
 * @returns {Object} - Object with dates as keys and aggregated values
 */
export const groupByDay = (data, dateField, valueField, aggregator = null) => {
  if (!Array.isArray(data) || !dateField || !valueField) {
    return {};
  }

  const result = {};

  // Default aggregator uses the last value for each day
  const defaultAggregator = (values) => values[values.length - 1];
  const aggFn = aggregator || defaultAggregator;

  // First pass: group values by date
  const grouped = {};

  data.forEach((item) => {
    const fullDate = item[dateField];
    if (!fullDate) return;

    const datePart = getDatePart(fullDate);
    if (!datePart) return;

    if (!grouped[datePart]) {
      grouped[datePart] = [];
    }

    grouped[datePart].push(item[valueField]);
  });

  // Second pass: apply aggregator to each group
  Object.keys(grouped).forEach((date) => {
    result[date] = aggFn(grouped[date]);
  });

  return result;
};
